import { useEffect, useRef, useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { formatPhp } from "../lib/money";
import type { PurchaseHandover, PurchaseMilestoneInput, AgreementDocument as Document } from "../lib/api";
import { AgreementDocument } from "./AgreementDocument";
import { HandoverSummary } from "./ItemArrangement";
import { MilestoneSummary } from "./PurchaseMilestones";

export type PurchasePreview = {
  itemId: string;
  title: string;
  seller: string;
  qty: number;
  unitPrice: number;
  total: number;
  handover: PurchaseHandover | null;
  milestones?: PurchaseMilestoneInput[] | null;
  paymentMethod: "wallet";
  requestKey?: string; document?: Document | null; sellerOfferDocument?: Document | null; uncertain?: boolean;
};

export function PurchaseConfirmation({ purchase, busy, error, onClose, onConfirm }: {
  purchase: PurchasePreview;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const [reviewed, setReviewed] = useState(false);
  useEffect(() => {
    const modal = dialog.current;
    modal?.showModal();
    // [items.checkout](../../../docs/product/items.md#items-checkout): start review at the heading; payment is never the initial focused action.
    heading.current?.focus({ preventScroll: true });
    return () => modal?.close();
  }, []);

  return <dialog ref={dialog} aria-labelledby="purchase-confirmation-title"
    onCancel={(event) => { event.preventDefault(); if (!busy && !purchase.uncertain) onClose(); }}
    className="m-auto max-h-[90dvh] w-[calc(100%_-_2rem)] max-w-lg overflow-hidden rounded-2xl border border-line bg-white p-0 shadow-xl backdrop:bg-navy/50">
    <div className="flex max-h-[90dvh] flex-col">
      <header className="shrink-0 border-b border-line px-5 py-4 sm:px-6">
        <h2 ref={heading} tabIndex={-1} id="purchase-confirmation-title" className="text-xl font-bold leading-7 text-navy outline-none">Confirm your purchase</h2>
      </header>
      <div className="min-h-0 overflow-y-auto p-5 sm:p-6">
        <div className="rounded-xl border border-line p-4 text-navy">
          <h3 className="break-words text-lg font-semibold leading-6">{purchase.title}</h3>
          <dl className="mt-3 space-y-2 text-sm">
            <div className="flex justify-between gap-3"><dt className="shrink-0 text-mut">Seller</dt><dd className="min-w-0 break-words text-right">{purchase.seller}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-mut">Unit price</dt><dd className="tabular-nums">{formatPhp(purchase.unitPrice)}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-mut">Quantity</dt><dd>{purchase.qty}</dd></div>
            <div className="flex justify-between gap-3"><dt className="shrink-0 text-mut">Pay using</dt><dd className="text-right">Wallet balance</dd></div>
            <div className="flex flex-wrap items-baseline justify-between gap-2 border-t border-line pt-3"><dt className="font-semibold">Total to pay</dt><dd className="text-2xl font-bold tabular-nums">{formatPhp(purchase.total)}</dd></div>
          </dl>
        </div>
        {purchase.handover && <div className="mt-5"><HandoverSummary terms={purchase.handover} /></div>}
        <MilestoneSummary milestones={purchase.milestones} total={purchase.total} />
        {purchase.document && <>
          <AgreementDocument document={purchase.document} />
          {purchase.sellerOfferDocument && <details className="mt-3"><summary className="cursor-pointer py-2 text-sm font-semibold text-navy">Seller-offered document</summary><AgreementDocument document={purchase.sellerOfferDocument} /></details>}
          <label className="mt-4 flex items-start gap-3 text-sm leading-6 text-navy"><input type="checkbox" className="mt-1 h-4 w-4 shrink-0" checked={reviewed} onChange={event => setReviewed(event.target.checked)} />I reviewed this purchase agreement</label>
        </>}
      </div>
      <footer className="shrink-0 border-t border-line bg-white px-5 py-4 sm:px-6">
        {error && <p role="alert" className="mb-3 break-words text-sm text-brand-red">{error}</p>}
        {purchase.uncertain && <p className="mb-3 text-sm text-mut">The payment outcome is unknown. Retry this same checkout to recover its saved result.</p>}
        <div className="flex items-stretch justify-end gap-2">
          <button type="button" disabled={busy || !!purchase.document && !reviewed} onClick={onConfirm} className="btn-red h-auto min-h-11 min-w-0 flex-1 whitespace-normal px-3 py-2 text-sm disabled:opacity-50 sm:flex-none">
            {busy ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <ShieldCheck className="h-4 w-4 shrink-0" />}
            <span className="min-w-0">{busy ? "Processing payment…" : `Confirm and pay ${formatPhp(purchase.total)}`}</span>
          </button>
          <button type="button" disabled={busy || purchase.uncertain} onClick={onClose} className="btn-outline h-auto min-h-11 shrink-0 px-3 text-sm disabled:opacity-50">Cancel</button>
        </div>
      </footer>
    </div>
  </dialog>;
}
